import React, { useRef, useState } from 'react';
import '../styles/EnrollmentForm.css';
import Cashapp from "../assets/cashapp_icon2.png";
import Venmo from "../assets/venmo_icon.png";
import Zelle from "../assets/Zelle1.svg";
import Paypal from "../assets/paypal_icon.png";
import { Mail, MessageSquare, Phone } from 'lucide-react';
import emailjs from '@emailjs/browser';
import Swal from 'sweetalert2';
import { useSectionReveal } from '../hooks/useSectionReveal';

const EnrollmentForm = () => {
  const sectionRef = useRef(null);
  useSectionReveal(sectionRef);

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    message: '',
    contactMethod: 'email',
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const serviceId = process.env.REACT_APP_EMAILJS_SERVICE_ID;
    const templateId = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
    const publicKey =
      process.env.REACT_APP_EMAILJS_PUBLIC_KEY || process.env.REACT_APP_EMAILJS_USER_ID;

    if (!serviceId || !templateId || !publicKey) {
      Swal.fire({
        icon: 'error',
        title: 'Configuration error',
        text: 'Email is not configured. Please call or text 440-915-2518.',
        confirmButtonColor: '#d33',
        confirmButtonText: 'OK',
      });
      return;
    }

    const templateParams = {
      firstName: formData.firstName,
      lastName: formData.lastName,
      phone: formData.phone,
      email: formData.email,
      message: formData.message,
      contactMethod: formData.contactMethod,
    };

    emailjs
      .send(serviceId, templateId, templateParams, { publicKey })
      .then(() => {
        Swal.fire({
          icon: 'success',
          title: 'Enrollment Sent!',
          text: 'Your enrollment was sent. I will reach out to you soon.',
          confirmButtonColor: '#3085d6',
          confirmButtonText: 'OK',
        });
        setFormData({
          firstName: '',
          lastName: '',
          phone: '',
          email: '',
          message: '',
          contactMethod: 'email',
        });
      })
      .catch(() => {
        Swal.fire({
          icon: 'error',
          title: 'Oops!',
          text: 'Failed to send the enrollment. Please try again. If problem persists call/text my number 440-915-2518.',
          confirmButtonColor: '#d33',
          confirmButtonText: 'Retry',
        });
      });
  };

  return (
    <div className="enrollment-section" ref={sectionRef}>
      <form className="enrollment-form" onSubmit={handleSubmit}>
        <div className="form-section">
          <h2>Lesson Request Form</h2>
          <p>
            Fill out and submit your lesson request with the form below. I’ll be in touch shortly after on what times and dates work best based on your preference selections.
          </p>
        </div>

        <div className="form-group">
          <label htmlFor="firstName">First Name</label>
          <input
            type="text"
            id="firstName"
            name="firstName"
            value={formData.firstName}
            onChange={handleChange}
            placeholder="Enter your first name"
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="lastName">Last Name</label>
          <input
            type="text"
            id="lastName"
            name="lastName"
            value={formData.lastName}
            onChange={handleChange}
            placeholder="Enter your last name"
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="phone">Phone</label>
          <input
            type="tel"
            id="phone"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            placeholder="Enter your phone number"
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="email">Email</label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="Enter your email address"
            required
          />
        </div>

        <div className="form-group">
          <label>Preferred Contact Method:</label>
          <div className="contact-method-options">
            <label className={`contact-option ${formData.contactMethod === 'text' ? 'selected' : ''}`} htmlFor="textOption">
              <input
                type="radio"
                id="textOption"
                name="contactMethod"
                value="text"
                checked={formData.contactMethod === 'text'}
                onChange={handleChange}
              />
              <MessageSquare className="contact-icon" size={22} strokeWidth={2} aria-hidden />
              Text
            </label>
            <label className={`contact-option ${formData.contactMethod === 'email' ? 'selected' : ''}`} htmlFor="emailOption">
              <input
                type="radio"
                id="emailOption"
                name="contactMethod"
                value="email"
                checked={formData.contactMethod === 'email'}
                onChange={handleChange}
              />
              <Mail className="contact-icon" size={22} strokeWidth={2} aria-hidden />
              Email
            </label>
            <label className={`contact-option ${formData.contactMethod === 'call' ? 'selected' : ''}`} htmlFor="callOption">
              <input
                type="radio"
                id="callOption"
                name="contactMethod"
                value="call"
                checked={formData.contactMethod === 'call'}
                onChange={handleChange}
              />
              <Phone className="contact-icon" size={22} strokeWidth={2} aria-hidden />
              Call
            </label>
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="message">Message</label>
          <textarea
            id="message"
            name="message"
            value={formData.message}
            onChange={handleChange}
            placeholder="Type your message here"
            rows="5"
          ></textarea>
        </div>

        <button type="submit" className="submit-button">Submit</button>

        <div className="payment-options">
          <h2>Payment Options</h2>
          <p><strong>Cash</strong>, Venmo, Paypal, Cashapp, Zelle</p>
          <div className="payment-icons">
            <a
              href="https://account.venmo.com/u/Kyle-Zubricky?catchAll=u&catchAll=Kyle-Zubricky"
              target="_blank"
              rel="noopener noreferrer"
            >
              <img src={Venmo} alt="Venmo" />
            </a>
            <span className="payment-icon-static" title="PayPal — arrange with coach">
              <img src={Paypal} alt="" />
            </span>
            <span className="payment-icon-static" title="Cash App — arrange with coach">
              <img src={Cashapp} alt="" />
            </span>
            <span className="payment-icon-static" title="Zelle — arrange with coach">
              <img src={Zelle} alt="" />
            </span>
          </div>
        </div>
      </form>
    </div>
  );
};

export default EnrollmentForm;
